import { Line } from '@react-three/drei';
import React, { memo } from 'react';
import * as THREE from 'three';

import { WallAngleIndicatorProps } from '../../../@types/Components';
import { useMultiSelectToggleContext } from '../../../provider/MultiSelectToggleProvider';
import {
  FULL_CIRCLE_DEGREES,
  HALF_CIRCLE_DEGREES,
  QUARTER_CIRCLE_DEGREES,
  THETA_ANGLE_DIVISOR
} from '../../../utils/constants';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

// NEU: Wir erweitern die Props on-the-fly um innerCorner
type ExtendedProps = WallAngleIndicatorProps & { innerCorner?: THREE.Vector3 };

// Hilfsfunktion: Liest den echten gerichteten Kanten-Vektor aus (inkl. Traversierungs-Richtung)
const getEdgeVector = (edge: any): THREE.Vector2 => {
  const w = edge.wall || edge;
  const s = new THREE.Vector2(w.startPoint.x, w.startPoint.y);
  const e = new THREE.Vector2(w.endPoint.x, w.endPoint.y);
  const v = e.sub(s);
  // Wenn die Kante im Polygon rückwärts durchlaufen wird, Vektor umdrehen!
  if (edge.reversed) v.negate();
  return v.normalize();
};

// Hilfsfunktion: Liest den Zielpunkt der gerichteten Kante aus (für den Fallback-Origin)
const getEdgeEnd = (edge: any): THREE.Vector2 => {
  const w = edge.wall || edge;
  return edge.reversed ? new THREE.Vector2(w.startPoint.x, w.startPoint.y) : new THREE.Vector2(w.endPoint.x, w.endPoint.y);
};

const WallAngleIndicator = ({
  wall, // Dies ist eigentlich prevEdge aus Space.tsx
  nextWall, // Dies ist eigentlich nextEdge aus Space.tsx
  color = '#cf000f',
  lineWidth = 2,
  polygonOrientation = 0,
  innerCorner, // <-- Die verschobene Innenecke aus Space.tsx
}: ExtendedProps): React.JSX.Element | null => {
  const { showAngles } = useMultiSelectToggleContext();
  if (!showAngles) return null;

  // 1. Zeichnungs-Ursprung setzen (Exakte Innenecke)
  const origin = innerCorner ? new THREE.Vector2(innerCorner.x, innerCorner.y) : getEdgeEnd(wall);

  // 2. Die Traversierungs-Vektoren beider Wände berechnen
  // dirPrev zeigt AUF die Ecke ZU. dirNext zeigt von der Ecke WEG.
  const dirPrev = getEdgeVector(wall);
  const dirNext = getEdgeVector(nextWall);

  if (dirPrev.lengthSq() < 1e-9 || dirNext.lengthSq() < 1e-9) return null;

  // 3. Vektoren, die beide von der Ecke WEG zeigen
  const vBackward = dirPrev.clone().negate(); // Die vorherige Wand zurückschauen
  const vForward = dirNext.clone();           // Die nächste Wand vorwärtsschauen

  // 4. Winkel der Vektoren
  const startAngle = Math.atan2(vForward.y, vForward.x);
  let endAngle = Math.atan2(vBackward.y, vBackward.x);
  let sweep = 0;

  // 5. Innenwinkel berechnen: Wir sweepen IMMER in das Innere des Raumes!
  // Wenn der Raum CCW läuft (>= 0), ist "Innen" links von den Kanten (Sweep CCW)
  // Wenn der Raum CW läuft (-1), ist "Innen" rechts von den Kanten (Sweep CW)
  if (polygonOrientation >= 0) {
    if (endAngle < startAngle) endAngle += 2 * Math.PI;
    sweep = endAngle - startAngle;
    if (polygonOrientation === 0 && sweep > Math.PI) sweep -= 2 * Math.PI;
  } else {
    if (endAngle > startAngle) endAngle -= 2 * Math.PI;
    sweep = endAngle - startAngle;
  }

  const interiorAngle = Math.abs(sweep);

  // 6. Bogen-Punkte berechnen
  const radius = 2.2;
  const steps = THETA_ANGLE_DIVISOR;
  const points: THREE.Vector3[] = [];
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const theta = startAngle + t * sweep;
    points.push(
      new THREE.Vector3(
        origin.x + Math.cos(theta) * radius,
        origin.y + Math.sin(theta) * radius,
        0,
      ),
    );
  }

  // 7. Text exakt in die Mitte des Bogens schieben
  const midAngle = startAngle + sweep / 2;
  const textPosition = new THREE.Vector3(
    origin.x + Math.cos(midAngle) * 1.5, // 1.5 schiebt den Text perfekt zwischen Ecke und roten Bogen
    origin.y + Math.sin(midAngle) * 1.5,
    0,
  );

  const angleDegrees = interiorAngle * (HALF_CIRCLE_DEGREES / Math.PI);
  const tolerance = 0.5;
  const isRightAngle = Math.abs(Math.round(angleDegrees) - QUARTER_CIRCLE_DEGREES) < tolerance;

  const normalizedAngle = (angleDegrees + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;
  
  // Nutze ein fettes Bullet-Symbol für 90° statt eines winzigen Punktes!
  const displayedAngle = isRightAngle ? '•' : `${normalizedAngle.toFixed(0)}°`;

  return (
    <group
      name="group-wallAngleIndicator"
      renderOrder={RENDER_ORDERS.angleIndicator}
    >
      <Line
        name="angleIndicator-line"
        points={points}
        color={color}
        lineWidth={lineWidth}
        transparent
        renderOrder={RENDER_ORDERS.angleIndicator}
      />
      <RoundedBoxWithText
        position={textPosition}
        fontSize={0.8}
        boxOpacity={0.2}
        boxSize={[2, 1, 0]}
        textColor={color}
        text={displayedAngle}
      />
    </group>
  );
};

export default memo(WallAngleIndicator);
